/**
 * Delivery Reconciliation Engine
 * 
 * A read-only intelligence layer that evaluates contract delivery performance
 * and produces adjustment signals for downstream systems.
 * 
 * Key Principles:
 * - Never mutates state directly
 * - Never routes leads
 * - Only produces recommendations
 * - All adjustments are soft and proportional
 * 
 * Answers: "If nothing changes, will this contract be fulfilled on time?"
 */

import type { Contract } from '@/contexts/ContractsContext';

// --- Delivery Status Enum ---

export type DeliveryStatus = 
  | 'ON_TRACK'
  | 'BEHIND'
  | 'AT_RISK'
  | 'OVERDELIVERING';

// --- Configuration ---

export interface ReconciliationConfig {
  /** Gap ratio threshold for ON_TRACK status (default: 0.10 = 10%) */
  onTrackThreshold: number;
  /** Gap ratio threshold for BEHIND status (default: 0.25 = 25%) */
  behindThreshold: number;
  /** Minimum days remaining to consider for urgency (prevents division issues) */
  minDaysForUrgency: number;
}

export const DEFAULT_RECONCILIATION_CONFIG: ReconciliationConfig = {
  onTrackThreshold: 0.10,
  behindThreshold: 0.25,
  minDaysForUrgency: 1,
};

// --- Contract Evaluation Types ---

export interface ContractEvaluation {
  contractId: string;
  agentId: string;
  agentName: string;
  contractType: Contract['contractType'];
  deliveryStatus: DeliveryStatus;
  totalCommitted: number;
  expectedDeliveredByNow: number;
  actualDelivered: number;
  deliveryGap: number;
  gapRatio: number;
  daysRemaining: number;
  daysElapsed: number;
  totalDays: number;
  urgencyScore: number;
  leadsPerDayNeeded: number;
  /** Whether this contract is mathematically feasible to fulfill */
  isFeasible: boolean;
  feasibilityReason?: string;
}

// --- Reconciliation Summary ---

export interface ReconciliationSummary {
  totalContracts: number;
  activeContracts: number;
  onTrack: number;
  behind: number;
  atRisk: number;
  overdelivering: number;
  totalCommitted: number;
  totalDelivered: number;
  overallDeliveryRate: number;
  /** Contracts that are mathematically impossible to fulfill */
  infeasibleCount: number;
}

// --- System Recommendations ---

export interface SystemRecommendations {
  /** Agents who should get higher retry priority */
  increaseRetryPriority: Array<{
    agentId: string;
    agentName: string;
    reason: string;
    urgencyScore: number;
  }>;
  /** Agents who should be more likely to enter pools */
  expandPoolInclusion: Array<{
    agentId: string;
    agentName: string;
    reason: string;
    recommendedBoost: number;
  }>;
  /** Agents whose guaranteed minimum boost should increase */
  boostGuaranteedMinimum: Array<{
    agentId: string;
    agentName: string;
    currentGap: number;
    recommendedBoostIncrease: number;
  }>;
  /** Agents who are overdelivering and can have reduced pacing priority */
  reducePacing: Array<{
    agentId: string;
    agentName: string;
    excessDelivered: number;
  }>;
  /** System-level warnings */
  warnings: Array<{
    type: 'INFEASIBLE_CONTRACT' | 'DEMAND_EXCEEDS_SUPPLY' | 'MULTIPLE_AT_RISK';
    message: string;
    contractIds?: string[];
    agentIds?: string[];
  }>;
}

// --- Full Reconciliation Result ---

export interface ReconciliationResult {
  evaluatedAt: number;
  configUsed: ReconciliationConfig;
  summary: ReconciliationSummary;
  contractEvaluations: ContractEvaluation[];
  systemRecommendations: SystemRecommendations;
}

// --- Helper Functions ---

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Classify delivery status based on gap ratio
 */
function classifyDeliveryStatus(
  gapRatio: number,
  actualDelivered: number,
  expectedDeliveredByNow: number,
  config: ReconciliationConfig
): DeliveryStatus {
  // Overdelivering: delivered more than expected by now
  if (actualDelivered > expectedDeliveredByNow && expectedDeliveredByNow > 0) {
    return 'OVERDELIVERING';
  }
  
  // AT_RISK: gap ratio exceeds behind threshold
  if (gapRatio > config.behindThreshold) {
    return 'AT_RISK';
  }
  
  // BEHIND: gap ratio exceeds on-track threshold but under at-risk
  if (gapRatio > config.onTrackThreshold) {
    return 'BEHIND';
  }
  
  // ON_TRACK: within acceptable range
  return 'ON_TRACK';
}

/**
 * Evaluate a single contract's delivery health
 */
function evaluateContract(
  contract: Contract,
  now: number,
  config: ReconciliationConfig
): ContractEvaluation {
  const dayMs = 24 * 60 * 60 * 1000;
  
  // Time calculations
  const totalDays = Math.max(1, Math.ceil((contract.cycleEnd - contract.cycleStart) / dayMs));
  const daysElapsed = Math.max(0, (now - contract.cycleStart) / dayMs);
  const daysRemaining = Math.max(0, Math.ceil((contract.cycleEnd - now) / dayMs));
  
  // Delivery calculations
  const expectedDeliveredByNow = contract.totalLeadsCommitted * (daysElapsed / totalDays);
  const actualDelivered = contract.leadsDelivered;
  const deliveryGap = Math.max(0, expectedDeliveredByNow - actualDelivered);
  
  // Gap ratio: how far behind relative to total commitment
  const gapRatio = contract.totalLeadsCommitted > 0
    ? deliveryGap / contract.totalLeadsCommitted
    : 0;
  
  // Leads remaining and rate needed
  const leadsRemaining = Math.max(0, contract.totalLeadsCommitted - actualDelivered);
  const leadsPerDayNeeded = daysRemaining > 0
    ? leadsRemaining / daysRemaining
    : leadsRemaining;
  
  // Urgency score: normalized remaining leads vs time
  const urgencyScore = clamp(
    leadsRemaining / Math.max(daysRemaining, config.minDaysForUrgency) / 
    Math.max(contract.totalLeadsCommitted / totalDays, 0.1),
    0,
    1
  );
  
  // Feasibility check: can we realistically fulfill?
  // Assume max 5 leads/day per agent is aggressive but possible
  const maxFeasibleRate = 5;
  const isFeasible = leadsPerDayNeeded <= maxFeasibleRate || leadsRemaining === 0;
  const feasibilityReason = !isFeasible
    ? `Needs ${leadsPerDayNeeded.toFixed(1)} leads/day (${leadsRemaining} remaining in ${daysRemaining} days)`
    : undefined;
  
  // Classify status
  const deliveryStatus = classifyDeliveryStatus(
    gapRatio,
    actualDelivered,
    expectedDeliveredByNow,
    config
  );
  
  return {
    contractId: contract.id,
    agentId: contract.agentId,
    agentName: contract.agentName,
    contractType: contract.contractType,
    deliveryStatus,
    totalCommitted: contract.totalLeadsCommitted,
    expectedDeliveredByNow: Math.round(expectedDeliveredByNow * 10) / 10,
    actualDelivered,
    deliveryGap: Math.round(deliveryGap * 10) / 10,
    gapRatio: Math.round(gapRatio * 1000) / 1000,
    daysRemaining,
    daysElapsed: Math.round(daysElapsed * 10) / 10,
    totalDays,
    urgencyScore: Math.round(urgencyScore * 1000) / 1000,
    leadsPerDayNeeded: Math.round(leadsPerDayNeeded * 10) / 10,
    isFeasible,
    feasibilityReason,
  };
}

/**
 * Generate system recommendations based on contract evaluations
 */
function generateRecommendations(
  evaluations: ContractEvaluation[]
): SystemRecommendations {
  const recommendations: SystemRecommendations = {
    increaseRetryPriority: [],
    expandPoolInclusion: [],
    boostGuaranteedMinimum: [],
    reducePacing: [],
    warnings: [],
  };
  
  // Group evaluations by agent (agent may have multiple contracts)
  const agentEvaluations = new Map<string, ContractEvaluation[]>();
  for (const eval_ of evaluations) {
    const existing = agentEvaluations.get(eval_.agentId) || [];
    existing.push(eval_);
    agentEvaluations.set(eval_.agentId, existing);
  }
  
  // Track at-risk contracts for warnings
  const atRiskContracts: ContractEvaluation[] = [];
  const infeasibleContracts: ContractEvaluation[] = [];
  
  for (const eval_ of evaluations) {
    // Collect at-risk and infeasible
    if (eval_.deliveryStatus === 'AT_RISK') {
      atRiskContracts.push(eval_);
    }
    if (!eval_.isFeasible) {
      infeasibleContracts.push(eval_);
    }
    
    // Generate recommendations based on status
    switch (eval_.deliveryStatus) {
      case 'AT_RISK':
        // High priority interventions
        recommendations.increaseRetryPriority.push({
          agentId: eval_.agentId,
          agentName: eval_.agentName,
          reason: `Contract ${eval_.contractId} is AT_RISK with ${eval_.gapRatio * 100}% gap`,
          urgencyScore: eval_.urgencyScore,
        });
        
        recommendations.expandPoolInclusion.push({
          agentId: eval_.agentId,
          agentName: eval_.agentName,
          reason: `Needs ${eval_.leadsPerDayNeeded} leads/day to fulfill contract`,
          recommendedBoost: Math.min(0.5, eval_.urgencyScore * 0.7),
        });
        
        recommendations.boostGuaranteedMinimum.push({
          agentId: eval_.agentId,
          agentName: eval_.agentName,
          currentGap: eval_.deliveryGap,
          recommendedBoostIncrease: Math.min(0.3, eval_.gapRatio),
        });
        break;
        
      case 'BEHIND':
        // Moderate interventions
        recommendations.expandPoolInclusion.push({
          agentId: eval_.agentId,
          agentName: eval_.agentName,
          reason: `Contract ${eval_.contractId} is BEHIND schedule by ${eval_.gapRatio * 100}%`,
          recommendedBoost: Math.min(0.3, eval_.urgencyScore * 0.4),
        });
        
        if (eval_.urgencyScore > 0.5) {
          recommendations.increaseRetryPriority.push({
            agentId: eval_.agentId,
            agentName: eval_.agentName,
            reason: `High urgency (${eval_.urgencyScore}) for behind contract`,
            urgencyScore: eval_.urgencyScore,
          });
        }
        break;
        
      case 'OVERDELIVERING':
        // Can reduce priority to balance system
        const excess = eval_.actualDelivered - eval_.expectedDeliveredByNow;
        if (excess > 2) { // Only if significantly overdelivering
          recommendations.reducePacing.push({
            agentId: eval_.agentId,
            agentName: eval_.agentName,
            excessDelivered: Math.round(excess * 10) / 10,
          });
        }
        break;
        
      case 'ON_TRACK':
        // No intervention needed
        break;
    }
  }
  
  // Generate system-level warnings
  if (infeasibleContracts.length > 0) {
    recommendations.warnings.push({
      type: 'INFEASIBLE_CONTRACT',
      message: `${infeasibleContracts.length} contract(s) are mathematically infeasible to fulfill`,
      contractIds: infeasibleContracts.map(c => c.contractId),
      agentIds: [...new Set(infeasibleContracts.map(c => c.agentId))],
    });
  }
  
  if (atRiskContracts.length >= 3) {
    recommendations.warnings.push({
      type: 'MULTIPLE_AT_RISK',
      message: `${atRiskContracts.length} contracts are at risk - system may be under-capacity`,
      contractIds: atRiskContracts.map(c => c.contractId),
      agentIds: [...new Set(atRiskContracts.map(c => c.agentId))],
    });
  }
  
  // Deduplicate recommendations per agent (keep highest priority)
  recommendations.increaseRetryPriority = deduplicateByAgent(
    recommendations.increaseRetryPriority,
    (a, b) => b.urgencyScore - a.urgencyScore
  );
  
  recommendations.expandPoolInclusion = deduplicateByAgent(
    recommendations.expandPoolInclusion,
    (a, b) => b.recommendedBoost - a.recommendedBoost
  );
  
  recommendations.boostGuaranteedMinimum = deduplicateByAgent(
    recommendations.boostGuaranteedMinimum,
    (a, b) => b.currentGap - a.currentGap
  );
  
  recommendations.reducePacing = deduplicateByAgent(
    recommendations.reducePacing,
    (a, b) => b.excessDelivered - a.excessDelivered
  );
  
  return recommendations;
}

/**
 * Deduplicate recommendations by agent, keeping highest priority
 */
function deduplicateByAgent<T extends { agentId: string }>(
  items: T[],
  compareFn: (a: T, b: T) => number
): T[] {
  const byAgent = new Map<string, T>();
  
  // Sort by priority first
  const sorted = [...items].sort(compareFn);
  
  // Keep first (highest priority) for each agent
  for (const item of sorted) {
    if (!byAgent.has(item.agentId)) {
      byAgent.set(item.agentId, item);
    }
  }
  
  return Array.from(byAgent.values());
}

/**
 * Build reconciliation summary from evaluations
 */
function buildSummary(evaluations: ContractEvaluation[]): ReconciliationSummary {
  let onTrack = 0;
  let behind = 0;
  let atRisk = 0;
  let overdelivering = 0;
  let totalCommitted = 0;
  let totalDelivered = 0;
  let infeasibleCount = 0;
  
  for (const eval_ of evaluations) {
    totalCommitted += eval_.totalCommitted;
    totalDelivered += eval_.actualDelivered;
    
    if (!eval_.isFeasible) infeasibleCount++;
    
    switch (eval_.deliveryStatus) {
      case 'ON_TRACK': onTrack++; break;
      case 'BEHIND': behind++; break;
      case 'AT_RISK': atRisk++; break;
      case 'OVERDELIVERING': overdelivering++; break;
    }
  }
  
  return {
    totalContracts: evaluations.length,
    activeContracts: evaluations.length, // All passed contracts are active
    onTrack,
    behind,
    atRisk,
    overdelivering,
    totalCommitted,
    totalDelivered,
    overallDeliveryRate: totalCommitted > 0
      ? Math.round((totalDelivered / totalCommitted) * 1000) / 10
      : 0,
    infeasibleCount,
  };
}

// --- Main Reconciliation Function ---

/**
 * Run delivery reconciliation on all active contracts
 * 
 * This is a pure function that produces read-only intelligence.
 * It does NOT mutate any state or route leads.
 * 
 * @param contracts - Array of active contracts to evaluate
 * @param now - Current timestamp (allows testing with fixed time)
 * @param config - Optional configuration overrides
 * @returns ReconciliationResult with evaluations and recommendations
 */
export function runDeliveryReconciliation(
  contracts: Contract[],
  now: number = Date.now(),
  config: ReconciliationConfig = DEFAULT_RECONCILIATION_CONFIG
): ReconciliationResult {
  // Filter to active contracts only
  const activeContracts = contracts.filter(c => c.status === 'active');
  
  // Evaluate each contract
  const contractEvaluations = activeContracts.map(contract =>
    evaluateContract(contract, now, config)
  );
  
  // Sort by urgency (highest first)
  contractEvaluations.sort((a, b) => b.urgencyScore - a.urgencyScore);
  
  // Build summary
  const summary = buildSummary(contractEvaluations);
  
  // Generate recommendations
  const systemRecommendations = generateRecommendations(contractEvaluations);
  
  // Log for development (optional)
  if (process.env.NODE_ENV === 'development') {
    console.log('[DeliveryReconciliation] Run completed:', {
      evaluatedAt: new Date(now).toISOString(),
      summary,
      atRiskContracts: contractEvaluations.filter(e => e.deliveryStatus === 'AT_RISK').length,
      recommendationsGenerated: {
        retryPriority: systemRecommendations.increaseRetryPriority.length,
        poolExpansion: systemRecommendations.expandPoolInclusion.length,
        guaranteedBoost: systemRecommendations.boostGuaranteedMinimum.length,
        reducePacing: systemRecommendations.reducePacing.length,
        warnings: systemRecommendations.warnings.length,
      },
    });
  }
  
  return {
    evaluatedAt: now,
    configUsed: config,
    summary,
    contractEvaluations,
    systemRecommendations,
  };
}

/**
 * Get the severity color/level for a delivery status
 */
export function getDeliveryStatusSeverity(status: DeliveryStatus): 'success' | 'warning' | 'error' | 'info' {
  switch (status) {
    case 'ON_TRACK': return 'success';
    case 'BEHIND': return 'warning';
    case 'AT_RISK': return 'error';
    case 'OVERDELIVERING': return 'info';
  }
}

/**
 * Get a human-readable label for a delivery status
 */
export function getDeliveryStatusLabel(status: DeliveryStatus): string {
  switch (status) {
    case 'ON_TRACK': return 'On Track';
    case 'BEHIND': return 'Behind';
    case 'AT_RISK': return 'At Risk';
    case 'OVERDELIVERING': return 'Overdelivering';
  }
}
